import { Typography } from '@mui/material'
import React, { Fragment, memo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UploadBGC } from 'src/constants/actionTypes'
import BGCIcon from 'src/assets/icons/bgc.svg'
import { Formik } from 'formik'
import {
  BGCManualUploadFormSchema,
  BGCMVRManualUploadFormSchema,
} from './BGCManualUploadForm.validator'
import { FormikFileInput } from '../shared/form/formik/FormikFileInput'
import { BGCManualUploadFormValues } from './BGCManualUploadForm.interface'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { BGCProceedWithYardstik } from './BGCProceedWithYardstik'
import { BGCCollectDataStep } from 'src/interfaces/collectData'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { MVRValidRules } from '../mvr/shared/MVRValidRules'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'
import { Button } from '../shared/Button'
import { SecureWrapper } from '../layout/SecureWrapper'
import { StepLabels } from '../client/step-labels/StepLabels'

interface Props {
  isYardstikEnabled?: boolean
  hideStepsCounter?: boolean
  onBack?: () => void
}

const initialValues: BGCManualUploadFormValues = {
  bgcFile: undefined,
  mvrFile: undefined,
}

type BGCFileOptionType = 'all_in_one' | 'separate'

interface BGCFileOption {
  value: BGCFileOptionType
  label: string
}

const fileOptions: BGCFileOption[] = [
  {
    value: 'all_in_one',
    label:
      'I want to upload one file containing both Background Check and Motor Vehicle Record Report',
  },
  {
    value: 'separate',
    label:
      'I want to upload Background Check and Motor Vehicle Record Report in two separate files',
  },
]

const BGCManualUploadComponent = ({ hideStepsCounter, isYardstikEnabled, onBack }: Props) => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep) as BGCCollectDataStep

  const [fileOptionType, setFileOptionType] = useState<BGCFileOptionType | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleFormSubmit = async (values: BGCManualUploadFormValues) => {
    setIsLoading(true)
    setError(null)

    const requestBody = {
      stepId: currentStep.id,
      files: [
        {
          file: values.bgcFile,
          name: 'background_check',
        },
      ],
    }

    if (values.mvrFile) {
      requestBody.files.push({
        file: values.mvrFile,
        name: 'mvr',
      })
    }

    try {
      await createAsyncAction(dispatch, UploadBGC.request(requestBody))
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={
        fileOptionType === 'separate' ? BGCMVRManualUploadFormSchema : BGCManualUploadFormSchema
      }
    >
      {({ handleSubmit }) => (
        <SecureWrapper
          onBack={onBack}
          footer={
            <Fragment>
              <FormErrorMessage css={tw`mb-4`} error={error} />
              <Button
                className={tw`w-full`}
                onClick={() => {
                  handleSubmit()
                }}
                loading={isLoading}
                data-testid="contractors-onboarding-bgc-manual-submit-btn"
              >
                Submit documents
              </Button>
            </Fragment>
          }
        >
          <div>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <img src={BGCIcon} />
            </div>
            <StepLabels hideStepIndex={hideStepsCounter} />
            <Typography css={tw`mb-6 mt-2`} variant="h2">
              Upload Background Check & Motor Vehicle Record
            </Typography>
            <Typography css={tw`text-shades-400`}>
              Once you submit the documents, you will automatically advance to the next step.
            </Typography>
            {isYardstikEnabled && <BGCProceedWithYardstik />}
            <div css={tw`my-2`}>
              <Typography css={tw`text-shades-400`} variant="overline">
                Select AN option
              </Typography>
            </div>
            <div css={tw`bg-shades-0 p-4 pt-2 rounded-2xl mb-8`}>
              <BasicRadioGroup
                value={fileOptionType}
                options={fileOptions}
                onChange={(_, value) => {
                  setFileOptionType(value as BGCFileOptionType)
                }}
                dataTestId="contractors-onboarding-bgc-manual-file-option-input"
              />
              {!!fileOptionType && (
                <div css={tw`mt-6`}>
                  <MVRValidRules />
                  <Typography css={tw`mb-1.5 text-shades-400`} variant="overline">
                    {fileOptionType === 'separate'
                      ? 'Upload Background Check File'
                      : 'Upload the file/s that contain a Background Check and Motor Vehicle Record'}
                  </Typography>
                  <FormikFileInput
                    name="bgcFile"
                    inputDataTestId="contractors-onboarding-bgc-manual-bgc-file-input"
                  />
                  {fileOptionType === 'separate' && (
                    <div css={tw`mt-6`}>
                      <Typography css={tw`mb-1.5 text-shades-400`} variant="overline">
                        Upload Motor Vehicle Record Report File
                      </Typography>
                      <FormikFileInput
                        name="mvrFile"
                        inputDataTestId="contractors-onboarding-bgc-manual-mvr-file-input"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </SecureWrapper>
      )}
    </Formik>
  )
}

export const BGCManualUpload = memo(BGCManualUploadComponent)
